<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";

import {
  onboardingMobileGateShown,
  onboardingPresetConfirmed,
  onboardingPresetSelected,
  onboardingSessionCreated,
  onboardingSessionCreationFailed,
  onboardingStarted,
} from "@/analytics/events/onboarding";
import { trackEvent } from "@/analytics/track";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import LoadingIndicator from "@/components/ui/LoadingIndicator.vue";
import { isSmallScreen } from "@/router/guards/utils";
import { useOnboardingStore } from "@/store/onboarding";
import { isCloud } from "@/utils/env/Environment";
import { setShortcutsEnabled } from "@/utils/shortcut/shortcuts";

import OnboardMobileGate from "./internal/OnboardMobileGate.vue";
import QuickStartPicker from "./internal/QuickStartPicker.vue";
import { useOnboardPresets } from "./internal/useOnboardPresets";
import { useOnboardSession } from "./internal/useOnboardSession";
import { useOnboardSuggestions } from "./internal/useOnboardSuggestions";

const router = useRouter();

// Preset loading
const { presets, isLoadingPresets, loadPresets } = useOnboardPresets();

// Preset generation
const { generateFromPreset, generateFallback } = useOnboardSuggestions();

// Session creation
const { createSession } = useOnboardSession();

// UI state
const isSubmitting = ref(false);
const showErrorDialog = ref(false);
const showMobileGate = ref(false);

async function handleSelectPreset(selectedPresetId: string) {
  if (isSubmitting.value) return;

  const preset = presets.value.find((p) => p.id === selectedPresetId);
  const result = preset ? generateFromPreset(preset) : generateFallback();

  trackEvent(onboardingPresetConfirmed(result.presetId));

  isSubmitting.value = true;
  showErrorDialog.value = false;

  try {
    const sessionId = await createSession({
      sessionName: result.sessionName,
      teams: result.teams,
      iterations: result.iterations,
      presetId: result.presetId,
    });

    trackEvent(
      onboardingSessionCreated(result.teams.length, result.iterations.length, result.presetId),
    );
    useOnboardingStore().enroll();

    if (isSmallScreen() && isCloud) {
      isSubmitting.value = false;
      showMobileGate.value = true;
      trackEvent(onboardingMobileGateShown());
      return;
    }

    await router.push({
      name: "app",
      params: { session: sessionId, team: "initial", board: "program" },
    });
  } catch {
    trackEvent(onboardingSessionCreationFailed(result.presetId));
    isSubmitting.value = false;
    showErrorDialog.value = true;
  }
}

function handleErrorDismiss() {
  showErrorDialog.value = false;
}

function handlePresetClicked(clickedPresetId: string) {
  trackEvent(onboardingPresetSelected(clickedPresetId));
}

onMounted(() => {
  setShortcutsEnabled(false);
  void loadPresets();
  trackEvent(onboardingStarted());
});

onBeforeUnmount(() => {
  setShortcutsEnabled(true);
});
</script>

<template>
  <!-- Mobile Gate (after session creation on small screens) -->
  <OnboardMobileGate v-if="showMobileGate" />

  <!-- Quick Start Picker -->
  <QuickStartPicker
    v-if="!isSubmitting && !showErrorDialog && !showMobileGate"
    :presets
    :is-loading="isLoadingPresets"
    @select-preset="handleSelectPreset"
    @preset-clicked="handlePresetClicked"
  />

  <!-- Loading Dialog -->
  <div
    v-if="isSubmitting && !showErrorDialog"
    class="loading-overlay"
    aria-live="polite"
    aria-busy="true"
  >
    <div class="loading-card">
      <LoadingIndicator class="loading-spinner" />
      <p class="loading-message">{{ $t("onboarding.creatingSession") }}</p>
      <p class="loading-subtitle">{{ $t("onboarding.creatingSubtitle") }}</p>
    </div>
  </div>

  <!-- Error Dialog - Accessible -->
  <div
    v-if="showErrorDialog"
    class="error-overlay"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="onboard-error-title"
    aria-describedby="onboard-error-message"
  >
    <div class="error-card" role="document">
      <h2 id="onboard-error-title" class="error-title">
        {{ $t("onboarding.error.title") }}
      </h2>
      <p id="onboard-error-message" class="error-message">
        {{ $t("onboarding.error.message") }}
      </p>
      <div class="error-actions">
        <BaseButton variant="filled" @click="handleErrorDismiss">
          {{ $t("onboarding.error.tryAgain") }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";
@use "@/styles/_z-index" as zIndex;

.error-overlay {
  position: fixed;
  inset: 0;
  background: colors.$alpha-50;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: zIndex.$modal-overlay;
}

.error-card {
  background: colors.$background-ui;
  border-radius: 12px;
  padding: 40px;
  max-width: 400px;
  text-align: center;
}

.error-title {
  @include typography.large("semi-bold");

  margin: 0 0 8px;
}

.error-message {
  color: colors.$text-subtle;
  margin: 0 0 24px;
  line-height: 1.5;
}

.error-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.loading-overlay {
  position: fixed;
  inset: 0;
  background: colors.$alpha-50;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: zIndex.$modal-overlay;
}

.loading-card {
  background: colors.$background-ui;
  border-radius: 12px;
  padding: 48px 64px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.loading-spinner {
  font-size: 32px;
}

.loading-message {
  @include typography.large("medium");

  color: colors.$text;
  margin: 0;
}

.loading-subtitle {
  @include typography.medium("regular");

  color: colors.$text-subtle;
  margin: -16px 0 0;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .error-overlay,
  .loading-overlay {
    transition: opacity 0.1s ease;
  }
}
</style>
